import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { useRelatorioAM } from "@/hooks/useRelatorioAM";
import { IRelatorioAM } from "@/interfaces/IRelatorioAM";
import { convertBase64ToFile } from "@/utils/convertBase64ToFile";
import { formatMessageError } from "@/utils/formatMessageError";
import { UploadOutlined } from "@ant-design/icons";
import { App, Button, Upload, UploadFile } from "antd";
import { RcFile, UploadChangeParam } from "antd/lib/upload";
import { useState } from "react";

export function SubmitBackupRAM() {
  const { isOnline } = useNetworkStatus();
  const { message, notification } = App.useApp();
  const {
    addRelatorioAM,
    submitRelatorioAMApi,
    updateRelatorioAMApi,
    updateRelatorioAM,
    listaRelatorioAM,
  } = useRelatorioAM();
  const [isLoading, setIsLoading] = useState(false);

  const lerArquivoTexto = (file: RcFile): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(file, "ISO-8859-1");
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  const dummyRequest = ({ file, onSuccess }: any) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const handleChangeUpload = async (
    info: UploadChangeParam<UploadFile<any>>
  ) => {
    setIsLoading(true);
    message.loading({
      content: "Processando backup...",
      key: "loading",
      duration: 0,
    });

    try {
      const conteudoTexto = await lerArquivoTexto(
        info.file.originFileObj as RcFile
      );

      // Validar se é JSON válido
      if (!conteudoTexto.trim().startsWith("{")) {
        throw new Error("Arquivo não contém JSON válido");
      }

      const jsonObject = JSON.parse(conteudoTexto) as IRelatorioAM;

      // Validar estrutura básica
      if (!jsonObject.itens || !Array.isArray(jsonObject.itens)) {
        throw new Error("Estrutura do backup inválida");
      }

      const formData = new FormData();

      const request: IRelatorioAM = {
        ...jsonObject,
        itens: jsonObject.itens.map((item) => ({
          ...item,
          anexos:
            item.anexos?.map((anexo) => ({
              nomeArquivo: anexo.nomeArquivo,
              nomeUpload: anexo.nomeUpload,
            })) || [],
        })),
      };

      // Converter anexos base64 para File e adicionar ao FormData
      const anexosRequest: File[] = [];

      jsonObject.itens.forEach((itemRelatorio) => {
        if (itemRelatorio.anexos && itemRelatorio.anexos.length > 0) {
          itemRelatorio.anexos.forEach((anexo) => {
            if (anexo.arquivoBase64 && anexo.nomeArquivo) {
              try {
                const arquivo = convertBase64ToFile(
                  anexo.arquivoBase64,
                  anexo.nomeArquivo
                );

                if (arquivo) {
                  anexosRequest.push(arquivo);
                }
              } catch (conversionError) {
                console.warn(
                  `Erro ao converter anexo ${anexo.nomeArquivo}:`,
                  conversionError
                );
              }
            }
          });
        }
      });

      anexosRequest.forEach((arquivo) => {
        formData.append("anexos", arquivo, arquivo.name);
      });

      if (request.id) {
        // Se o relatório já tem ID, é uma atualização
        const requestUpdated = {
          ...request,
          itens: request.itens.filter((item) => item.alternativa),
        };

        formData.append(
          "ramRequest",
          new Blob([JSON.stringify(requestUpdated)], {
            type: "application/json",
          })
        );

        const result = await updateRelatorioAMApi(formData, request.id);

        const relatorioOffline = listaRelatorioAM.find(
          (ram) => ram.id === result.id
        );

        if (relatorioOffline) {
          updateRelatorioAM({
            ...result,
            offlineId: relatorioOffline.offlineId,
            isUpdated: true,
          });
        } else {
          addRelatorioAM({
            ...result,
            id: result.id,
            isUpdated: true,
          });
        }

        message.success({
          content: "Backup importado e relatório atualizado com sucesso!",
          key: "loading",
        });

        return;
      }

      formData.append(
        "ramRequest",
        new Blob([JSON.stringify(request)], {
          type: "application/json",
        })
      );

      const result = await submitRelatorioAMApi(formData);

      addRelatorioAM({
        ...result,
        isUpdated: true,
      });
      message.success({
        content: "Backup importado e relatório enviado com sucesso!",
        key: "loading",
      });

      setIsLoading(false);
    } catch (error) {
      console.error("Erro ao processar backup:", error);
      setIsLoading(false);
      message.destroy("loading");
      const messageError = formatMessageError(error);
      notification.error({
        message: "Erro ao processar backup",
        description: messageError,
        key: "loading",
      });
    }
  };

  return (
    <Upload
      customRequest={dummyRequest}
      onChange={handleChangeUpload}
      fileList={[]}
      accept="text/plain, .txt"
    >
      <Button disabled={!isOnline || isLoading} icon={<UploadOutlined />}>
        Enviar Backup
      </Button>
    </Upload>
  );
}
